import { useState, useCallback } from 'react';
import api from '../api/axios';
import useLocale from './useLocale';

function useReview(reviewId) {
  const [pdfData, setPdfData] = useState(null); // URL для PDF (blob:ObjectURL)
  const [pdfError, setPdfError] = useState(null); // { status, detail } when PDF load fails
  const [reviewStatus, setReviewStatus] = useState(null);
  const [reviewText, setReviewText] = useState(null);
  const [reviewTemplateId, setReviewTemplateId] = useState(null);
  const [template, setTemplate] = useState(null);

  // Раньше было thesis, теперь article
  const [article, setArticle] = useState(null);

  const { getCurrentLanguage } = useLocale();

  /**
   * Загрузить PDF конкретной версии статьи.
   * Используется CommentPage при переключении версии.
   */
  const loadPdfForVersion = useCallback(async (articleId, articleVersionId) => {
    if (!articleId || !articleVersionId) return;

    try {
      const ts = Date.now();
      const response = await api.get(
        `/articles/${articleId}.pdf?article_version_id=${articleVersionId}&ts=${ts}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfData(url);
      setPdfError(null);
    } catch (error) {
      console.error('Ошибка загрузки PDF конкретной версии статьи:', error);
      setPdfData(null);
      setPdfError({
        status: error?.response?.status ?? null,
        detail: error?.response?.data?.detail ?? null,
      });
    }
  }, []);

  const loadReview = useCallback(async () => {
    try {
      // 1) Получаем ревью + статью
      //   backend теперь отдаёт ArticleReviewDTO: { article, review }
      const response = await api.get(`/reviews/${reviewId}?encode=pdf`);

      const { article: articleDetails, review } = response.data || {};

      setReviewStatus(review?.status ?? null);
      setReviewText(review?.text ?? null);
      setReviewTemplateId(review?.templateId ?? null);
      setTemplate(review?.template ?? null);

      setArticle(articleDetails || null);

      // 2) Пробуем живой PDF статьи по article.id (текущая версия)
      const articleId = articleDetails?.id;
      let pdfUrlFromArticle = null;
      let pdfErrorLocal = null;

      if (articleId) {
        try {
          const ts = Date.now();
          const r = await api.get(`/articles/${articleId}.pdf?ts=${ts}`, {
            responseType: 'blob',
          });
          const blob = new Blob([r.data], { type: 'application/pdf' });
          pdfUrlFromArticle = URL.createObjectURL(blob);
          pdfErrorLocal = null;
        } catch (e) {
          // keep null pdf, but store the status so UI can show correct placeholder
          pdfUrlFromArticle = null;
          pdfErrorLocal = {
            status: e?.response?.status ?? null,
            detail: e?.response?.data?.detail ?? null,
          };
        }
      }

      // 3) Фолбек: если живого PDF нет - пробуем base64 из article.text (encode=pdf)
      let finalPdfUrl = pdfUrlFromArticle;
      if (!finalPdfUrl && articleDetails?.text) {
        const lang = getCurrentLanguage();
        const raw = (articleDetails.text?.[lang] ?? articleDetails.text?.en ?? '').trim();

        const asPdfUrlOrNull = (s) => {
          if (!s) return null;
          try {
            const bin = atob(s);
            if (!bin.startsWith('%PDF')) return null;
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
            return URL.createObjectURL(pdfBlob);
          } catch {
            return null;
          }
        };

        finalPdfUrl = asPdfUrlOrNull(raw);
      }

      setPdfData(finalPdfUrl || null);
      setPdfError(finalPdfUrl ? null : pdfErrorLocal);
    } catch (error) {
      console.error('Ошибка при загрузке ревью:', error);
    }
  }, [reviewId, getCurrentLanguage]);

  // Локальное обновление состояния отзыва без повторного запроса на сервер.
  const updateReviewLocally = useCallback((newStatus, newFormValues) => {
    if (newStatus !== undefined && newStatus !== null) {
      setReviewStatus(newStatus);
    }
    if (newFormValues) {
      setReviewText(newFormValues);
    }
  }, []);

  return {
    pdfData,
    pdfError,
    loadReview,
    reviewStatus,
    reviewText,
    reviewTemplateId,
    template,
    article,
    loadPdfForVersion,
    updateReviewLocally,
  };
}

export default useReview;
